<template>
  <ProductCard
    :id="id"
    class="wishlist-product-card group relative col-span-12 border-b border-neutral-20 px-2 py-3 sm:col-span-6 sm:border-r sm:px-4 sm:py-5 lg:col-span-4"
    :image="image"
    :title="title"
    :name="name"
    :link="link"
    :loading="false"
    :is-available="isAvailable"
    :product="entry.product"
    :price="price"
    placement="PDP-carousel"
    footer-classes="flex justify-between items-end mt-auto"
    price-align-classes="flex justify-start"
    @click:select-item="$emit('click:select-item')"
  >
    <template #header-actions>
      <div v-if="!isAvailable" class="absolute left-0 top-0 p-2">
        <p class="pr-3 text-base">
          {{ $t('global.sold_out') }}
        </p>
      </div>
      <div class="absolute right-3 top-3 z-10 md:right-6 md:top-3">
        <WishlistToggle
          show-close-icon
          :product="entry.product"
          :variant="variant"
        />
      </div>
    </template>
    <template #footer-action>
      <FimButton
        :no-padding="true"
        class="rounded-full fill-current p-2 text-white"
        data-testid="favorites_button_toPDP"
        @click="goToPDP"
      >
        <LazyIconFielmannArrowRight class="size-7" />
      </FimButton>
    </template>
    <template #links>
      <FimButton
        v-if="ropoSupport"
        no-padding
        type="link-alternative"
        class="mt-4"
        data-testid="wishlist-ropo-add-cta"
        @click="addProductToRopo"
      >
        <span>
          {{ $t('ropo.add_to_basket.cta') }}
        </span>
      </FimButton>
    </template>
  </ProductCard>
</template>

<script setup lang="ts">
import type { RouteLocationRaw } from '#vue-router'
import type { Price, ProductImage, Variant } from '@scayle/storefront-nuxt'
import type { FimWishlistItem } from '~/composables/useFimWishlist'

const props = defineProps({
  id: {
    type: Number as PropType<number>,
    required: true,
  },
  link: {
    type: String as PropType<RouteLocationRaw>,
    default: () => {},
  },
  image: {
    type: Object as PropType<ProductImage>,
    default: () => {},
  },
  entry: {
    type: Object as PropType<FimWishlistItem>,
    default: () => {},
  },
  price: {
    type: Object as PropType<Price>,
    default: () => {},
  },
  variants: {
    type: Array as PropType<Variant[]>,
    default: () => [],
  },
  size: {
    type: String as PropType<string>,
    required: true,
  },
  title: {
    type: String as PropType<string>,
    required: true,
  },
  name: {
    type: String as PropType<string>,
    required: true,
  },
  isInBasket: {
    type: Boolean as PropType<boolean>,
    default: () => false,
  },
  isAvailable: {
    type: Boolean as PropType<boolean>,
    default: () => true,
  },
})

const { isRopoEnabled } = await useFeatureToggles()
const localePath = useFimLocalePath()
const router = useRouter()
const goToPDP = () => router.push(localePath(props.link as object))

const { addProductToRopoBasket, isRopoSupported } = await useRopoBasketState()

const ropoSupport = computed(
  () => isRopoEnabled.value && isRopoSupported(props.entry.product),
)

const emit = defineEmits(['click:select-item', 'click:ropo-add'])

const product = computed(() => props.entry.product)
const variant = computed(() => {
  const fallback = getCheapestVariant(product.value)
  if (props.entry.variantId) {
    return (
      product.value.variants?.find((v) => v.id === props.entry.variantId) ||
      fallback
    )
  }

  return fallback
})

const addProductToRopo = () => {
  addProductToRopoBasket(product.value, variant.value)
  emit('click:ropo-add')
}
</script>

<style lang="scss">
.wishlist-product-card {
  article {
    width: 100%;
    max-width: 60rem;
    display: flex;
    flex-direction: column;
  }
}
</style>
